// src/App.tsx
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from "react-router-dom";

import { DataProvider, useData, ModuleKey, Role } from "./DataContext";
import AppShell from "./components/AppShell";

import DashboardPage from "./pages/DashboardPage";
import ClientsPage from "./pages/ClientsPage";
import ProjectsPage from "./pages/ProjectsPage";
import ProjectDetailPage from "./pages/ProjectDetailPage";
import ExpensesPage from "./pages/ExpensesPage";
import SubContractsPage from "./pages/SubContractsPage";
import PaymentsReceivedPage from "./pages/PaymentsReceivedPage";
import ResourcesPage from "./pages/ResourcesPage";
import WagesPage from "./pages/WagesPage";
import PartnerDisbursementsPage from "./pages/PartnerDisbursementsPage";
import ResetDataPage from "./pages/ResetDataPage";
import AdminTenantPage from "./pages/AdminTenantPage";
import InvoicesPage from "./pages/InvoicesPage";
import InvoiceDetailPage from "./pages/InvoiceDetailPage";
import MyCompanyPage from "./pages/MyCompanyPage";
import SchedulePage from "./pages/SchedulePage";
import MaterialsPage from "./pages/MaterialsPage";
import QuickStartPage from "./pages/QuickStartPage";
import TranslationReviewPage from "./pages/TranslationReviewPage";

function ModuleBlocked({ module }: { module: ModuleKey }) {
  const { t: tr } = useTranslation();
  return (
    <div className="container" style={{ maxWidth: 720 }}>
      <div className="card">
        <h2 style={{ marginTop: 0 }}>{tr("common.moduleNotEnabled")}</h2>
        <p style={{ color: "var(--muted)" }}>
          The <b>{module}</b> module isn’t enabled for this workspace.
        </p>
        <div className="pageNote">
          Ask an admin to enable this module for your tenant.
        </div>
      </div>
    </div>
  );
}

function RequireModule({
  module,
  allowed,
  children,
}: {
  module: ModuleKey;
  allowed: boolean;
  children: React.ReactNode;
}) {
  if (!allowed) return <ModuleBlocked module={module} />;
  return <>{children}</>;
}

function RequireRole({
  role,
  allow,
  children,
}: {
  role: Role;
  allow: Role;
  children: React.ReactNode;
}) {
  const { t: tr } = useTranslation();
  if (role !== allow) {
    return (
      <div className="container" style={{ maxWidth: 720 }}>
        <div className="card">
          <h2 style={{ marginTop: 0 }}>{tr("common.accessDenied")}</h2>
          <p style={{ color: "var(--muted)" }}>
            This page requires <b>{allow}</b> access.
          </p>
        </div>
      </div>
    );
  }
  return <>{children}</>;
}

function LoginScreen() {
  const { t: tr } = useTranslation();
  const navigate = useNavigate();
  const { setAuthSession, clearAuthSession, switchTenant } = useData() as any;

  const [tenantCode, setTenantCode] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [mustChange, setMustChange] = useState(false);
  const [currentPassword, setCurrentPassword] = useState(""); // used for change_password API
  const [newPassword, setNewPassword] = useState("");
  const [confirmNew, setConfirmNew] = useState("");

  const [pendingIsSystem, setPendingIsSystem] = useState(false);

  const passwordMeetsPolicy = (p: string) => {
    const s = (p || "").trim();
    if (s.length < 8) return false;
    if (!/[0-9]/.test(s)) return false;
    return true;
  };

  async function postJson<T>(url: string, payload: any): Promise<T> {
    const res = await fetch(url, {
      method: "POST",
      headers: { "content-type": "application/json" },
      credentials: "include",
      body: JSON.stringify(payload),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || (data as any)?.ok === false) {
      throw new Error((data as any)?.error || `Request failed (${res.status})`);
    }
    return data as T;
  }

  function looksLikeMustChange(msg: string) {
    const m = (msg || "").toLowerCase();
    return (
      m.includes("password change required") ||
      m.includes("must change password") ||
      m.includes("must_change_password") ||
      (m.includes("password") && m.includes("change") && m.includes("required"))
    );
  }

  const doLogin = async () => {
    setError(null);
    setBusy(true);

    try {
      const t = (tenantCode || "").trim();
      const u = (username || "").trim();
      const p = (password || "").trim();

      if (!t) throw new Error("Enter tenant code.");
      if (!u) throw new Error("Enter username.");
      if (!p) throw new Error("Enter password.");

      // First try normal login
      const resp = await postJson<{
        ok: true;
        data: {
          tenantId: string;
          userId: string;
          username: string;
          mustChangePassword: boolean;
          isSystem?: boolean;
        };
      }>("/api/auth/login", { tenantCode: t, username: u, password: p });

      const { tenantId, mustChangePassword } = resp.data;
      const isSystem = Boolean(resp.data.isSystem);

      // Always scope API calls to the tenant we authenticated against
      switchTenant(tenantId);

      // Preferred flow (backend returns mustChangePassword=true)
      if (mustChangePassword) {
        setMustChange(true);
        setCurrentPassword(p);
        setPassword("");
        setPendingIsSystem(isSystem);
        setError(null);
        return;
      }

      const role: Role = isSystem ? "admin" : "tenant";

      setAuthSession({
        role,
        tenantId,
        userId: resp.data.userId,
        username: u,
        createdAt: new Date().toISOString(),
      });

      navigate(isSystem ? "/admin" : "/dashboard", { replace: true });
    } catch (e: any) {
      const msg = e?.message || "Login failed.";

      // ✅ IMPORTANT FIX:
      // If backend is throwing an error instead of returning mustChangePassword,
      // switch UI into the change-password screen.
      if (looksLikeMustChange(msg)) {
        const p = (password || "").trim();

        // We don't know tenantId from error; but change_password resolves by tenantCode anyway.
        // Still set tenant scope for future API calls AFTER change_password succeeds.
        setMustChange(true);
        setCurrentPassword(p);
        setPassword("");
        setPendingIsSystem(false);
        setError(null);
        return;
      }

      setError(msg);
      clearAuthSession();
    } finally {
      setBusy(false);
    }
  };

  const doChangePassword = async () => {
    setError(null);
    setBusy(true);

    try {
      const t = (tenantCode || "").trim();
      const u = (username || "").trim();
      const cur = (currentPassword || "").trim();
      const p1 = (newPassword || "").trim();
      const p2 = (confirmNew || "").trim();

      if (!t) throw new Error("Missing tenant code.");
      if (!u) throw new Error("Missing username.");
      if (!cur) throw new Error("Missing current password.");
      if (!p1) throw new Error("Enter a new password.");
      if (p1 !== p2) throw new Error("New passwords do not match.");
      if (!passwordMeetsPolicy(p1)) {
        throw new Error(
          "Password must be at least 8 characters and include at least 1 number."
        );
      }

      // backend expects currentPassword
      const resp = await postJson<{
        ok: true;
        data: {
          tenantId: string;
          userId: string;
          username: string;
          mustChangePassword: boolean;
          isSystem?: boolean;
        };
      }>("/api/auth/change_password", {
        tenantCode: t,
        username: u,
        currentPassword: cur,
        newPassword: p1,
      });

      const { tenantId, userId } = resp.data;
      const isSystem = Boolean(resp.data.isSystem ?? pendingIsSystem);

      // Now that change_password succeeded, scope app tenant
      switchTenant(tenantId);

      const role: Role = isSystem ? "admin" : "tenant";

      // Session cookie already set by change_password endpoint
      setAuthSession({
        role,
        tenantId,
        userId,
        username: u,
        createdAt: new Date().toISOString(),
      });

      // clear UI state
      setMustChange(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmNew("");
      setPendingIsSystem(false);

      navigate(isSystem ? "/admin" : "/dashboard", { replace: true });
    } catch (e: any) {
      setError(e?.message || "Password change failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="container" style={{ maxWidth: 560 }}>
      <div className="card">
        <h1 style={{ marginTop: 0 }}>{tr("auth.appTitle")}</h1>
        <p style={{ color: "var(--muted)", marginTop: 0 }}>
          {tr("auth.appSubtitle")}
        </p>

        {!mustChange ? (
          <>
            <div style={{ marginTop: 14 }}>
              <label className="label">{tr("auth.tenantCodeLabel")}</label>
                <input
                  className="input"
                  value={tenantCode}
                  onChange={(e) => setTenantCode(e.target.value.toUpperCase())}
                  placeholder={tr("auth.tenantCodePlaceholder")}
                  autoCapitalize="off"
                  autoCorrect="off"
                />
            </div>

            <div style={{ marginTop: 10 }}>
              <label className="label">{tr("auth.usernameLabel")}</label>
              <input
                className="input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder={tr("auth.usernamePlaceholder")}
                autoCapitalize="off"
                autoCorrect="off"
              />
            </div>

            <div style={{ marginTop: 10 }}>
              <label className="label">{tr("auth.passwordLabel")}</label>
              <input
                className="input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={tr("auth.passwordPlaceholder")}
                type="password"
              />
            </div>

            <button
              className="btn"
              onClick={doLogin}
              style={{ width: "100%", marginTop: 12 }}
              type="button"
              disabled={busy}
            >
              {busy ? tr("auth.signingIn") : tr("auth.signIn")}
            </button>

            <div className="pageNote" style={{ marginTop: 12 }}>
              {tr("auth.firstLoginNote")}
            </div>
          </>
        ) : (
          <>
            <div className="pageNote" style={{ marginTop: 0 }}>
              {tr("auth.firstLoginTitle")}
              {pendingIsSystem ? ` ${tr("auth.firstLoginSystemAdmin")}` : null}
            </div>

            <div style={{ marginTop: 14 }}>
              <label className="label">{tr("auth.newPasswordLabel")}</label>
              <input
                className="input"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder={tr("auth.newPasswordPlaceholder")}
                type="password"
              />
            </div>

            <div style={{ marginTop: 10 }}>
              <label className="label">{tr("auth.confirmPasswordLabel")}</label>
              <input
                className="input"
                value={confirmNew}
                onChange={(e) => setConfirmNew(e.target.value)}
                placeholder={tr("auth.confirmPasswordPlaceholder")}
                type="password"
              />
            </div>

            <button
              className="btn"
              onClick={doChangePassword}
              style={{ width: "100%", marginTop: 12 }}
              type="button"
              disabled={busy}
            >
              {busy ? tr("auth.updating") : tr("auth.changePassword")}
            </button>

            <button
              className="btn btnGhost"
              style={{ width: "100%", marginTop: 10 }}
              type="button"
              disabled={busy}
              onClick={() => {
                setMustChange(false);
                setCurrentPassword("");
                setNewPassword("");
                setConfirmNew("");
                setPendingIsSystem(false);
                setError(null);
              }}
            >
              {tr("auth.backToLogin")}
            </button>
          </>
        )}

        {error ? (
          <div
            className="pageNote"
            style={{ marginTop: 12, color: "var(--danger)" }}
          >
            {error}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Inner app so we can use useData() safely inside DataProvider.
 */
function AppRoutes() {
  const { t: tr } = useTranslation();
  const { authSession, clearAuthSession, hasModule } = useData();

  const loggedIn = Boolean(authSession?.role);
  const authRole: Role = (authSession?.role || "tenant") as Role;

  const handleLogout = async () => {
    const ok = window.confirm(tr("auth.logoutConfirm"));
    if (!ok) return;

    try {
      await fetch("/api/auth/logout", {
        method: "POST",
        credentials: "include",
      }).catch(() => {});
    } finally {
      clearAuthSession();
      if (typeof window !== "undefined") window.location.href = "/";
    }
  };

  if (!loggedIn) return <LoginScreen />;

  return (
    <AppShell onLogout={handleLogout} authRole={authRole}>
      <Routes>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />

        {/* Core */}
        <Route
          path="/dashboard"
          element={
            <RequireModule module="core" allowed={hasModule("core")}>
              <DashboardPage />
            </RequireModule>
          }
        />

        <Route
          path="/my-company"
          element={
            <RequireModule module="core" allowed={hasModule("core")}>
              <MyCompanyPage />
            </RequireModule>
          }
        />

        {/* Admin (OWNER-ONLY, not a sellable module) */}
        <Route
          path="/admin"
          element={
            <RequireRole role={authRole} allow="admin">
              <AdminTenantPage />
            </RequireRole>
          }
        />

        {/* Setup */}
        <Route
          path="/clients"
          element={
            <RequireModule module="clients" allowed={hasModule("clients")}>
              <ClientsPage />
            </RequireModule>
          }
        />

        <Route
          path="/projects"
          element={
            <RequireModule module="projects" allowed={hasModule("projects")}>
              <ProjectsPage />
            </RequireModule>
          }
        />
        <Route
          path="/projects/:projectId"
          element={
            <RequireModule module="projects" allowed={hasModule("projects")}>
              <ProjectDetailPage />
            </RequireModule>
          }
        />

        {/* Finance */}
        <Route
          path="/expenses"
          element={
            <RequireModule module="expenses" allowed={hasModule("expenses")}>
              <ExpensesPage />
            </RequireModule>
          }
        />

        <Route
          path="/subcontracts"
          element={
            <RequireModule
              module="subcontracts"
              allowed={hasModule("subcontracts")}
            >
              <SubContractsPage />
            </RequireModule>
          }
        />

        <Route
          path="/payments-received"
          element={
            <RequireModule module="payments" allowed={hasModule("payments")}>
              <PaymentsReceivedPage />
            </RequireModule>
          }
        />

        <Route
          path="/invoices"
          element={
            <RequireModule module="invoices" allowed={hasModule("invoices")}>
              <InvoicesPage />
            </RequireModule>
          }
        />

        <Route
          path="/invoices/:id"
          element={
            <RequireModule module="invoices" allowed={hasModule("invoices")}>
              <InvoiceDetailPage />
            </RequireModule>
          }
        />

        {/* Ops */}
        <Route
          path="/schedule"
          element={
            <RequireModule module="core" allowed={hasModule("core")}>
              <SchedulePage />
            </RequireModule>
          }
        />

        <Route
          path="/materials"
          element={
            <RequireModule module="core" allowed={hasModule("core")}>
              <MaterialsPage />
            </RequireModule>
          }
        />

        <Route
          path="/resources"
          element={
            <RequireModule module="resources" allowed={hasModule("resources")}>
              <ResourcesPage />
            </RequireModule>
          }
        />

        <Route
          path="/wages"
          element={
            <RequireModule module="wages" allowed={hasModule("wages")}>
              <WagesPage />
            </RequireModule>
          }
        />

        {/* Partners */}
        <Route
          path="/partner-disbursements"
          element={
            <RequireModule module="partners" allowed={hasModule("partners")}>
              <PartnerDisbursementsPage />
            </RequireModule>
          }
        />

        {/* Utility */}
        <Route
          path="/reset-data"
          element={
            <RequireRole role={authRole} allow="admin">
              <ResetDataPage />
            </RequireRole>
          }
        />

        {/* Quick Start */}
        <Route path="/quick-start" element={<QuickStartPage />} />

        {/* Translation Review — NJDC tenant only (sidebar gated, but route open) */}
        <Route path="/translation-review" element={<TranslationReviewPage />} />

        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </AppShell>
  );
}

export default function App() {
  return (
    <DataProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </DataProvider>
  );
}